import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import toast from 'react-hot-toast';

interface Announcement {
  id: string;
  title: string;
  content: string;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
}

const blank = { title: '', content: '', is_active: true, expires_at: '' };

export default function AdminAnnouncementsPage() {
  const qc = useQueryClient();
  const [modal, setModal] = useState<{ mode: 'create' | 'edit'; data: typeof blank & { id?: string } } | null>(null);

  const { data, isLoading } = useQuery('admin-announcements',
    () => apiClient.get<any>('/admin/announcements')
  );

  const onSuccess = () => { qc.invalidateQueries('admin-announcements'); toast.success('Saved'); setModal(null); };

  const createMut = useMutation((body: any) => apiClient.post('/admin/announcements', body),     { onSuccess });
  const updateMut = useMutation(({ id, ...body }: any) => apiClient.put(`/admin/announcements/${id}`, body), { onSuccess });
  const deleteMut = useMutation((id: string) => apiClient.delete(`/admin/announcements/${id}`),  { onSuccess: () => { qc.invalidateQueries('admin-announcements'); toast.success('Deleted'); } });

  const handleSubmit = () => {
    if (!modal) return;
    const payload = {
      title:      modal.data.title,
      content:    modal.data.content,
      is_active:  modal.data.is_active,
      expires_at: modal.data.expires_at || null,
    };
    if (modal.mode === 'create') createMut.mutate(payload);
    else updateMut.mutate({ id: modal.data.id, ...payload });
  };

  const announcements: Announcement[] = data?.announcements ?? [];

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-1">Announcements</h1>
          <p className="text-sm text-gray-500 dark:text-zinc-500">Manage homepage banners</p>
        </div>
        <button onClick={() => setModal({ mode: 'create', data: { ...blank } })}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors">
          <Plus className="h-4 w-4" /> New Announcement
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-3">{[...Array(4)].map((_,i) => <div key={i} className="h-24 rounded-xl bg-gray-100 dark:bg-zinc-800 animate-pulse" />)}</div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-12 text-gray-400 dark:text-zinc-600">No announcements yet.</div>
      ) : (
        <div className="space-y-3">
          {announcements.map(a => (
            <div key={a.id} className={`bg-white dark:bg-zinc-900 rounded-xl border p-5 ${a.is_active ? 'border-brand-200 dark:border-brand-800' : 'border-gray-200 dark:border-zinc-800'}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">{a.title}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${a.is_active ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-zinc-500'}`}>
                      {a.is_active ? 'Live' : 'Draft'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-zinc-400 line-clamp-2">{a.content}</p>
                  <p className="text-xs text-gray-400 dark:text-zinc-600 mt-2">
                    Created {new Date(a.created_at).toLocaleDateString()}
                    {a.expires_at && ` · Expires ${new Date(a.expires_at).toLocaleDateString()}`}
                  </p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => updateMut.mutate({ id: a.id, is_active: !a.is_active })} title={a.is_active ? 'Deactivate' : 'Activate'} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500">
                    {a.is_active ? <ToggleRight className="h-5 w-5 text-green-500" /> : <ToggleLeft className="h-5 w-5" />}
                  </button>
                  <button onClick={() => setModal({ mode: 'edit', data: { id: a.id, title: a.title, content: a.content, is_active: a.is_active, expires_at: a.expires_at ?? '' } })} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => { if (window.confirm('Delete this announcement?')) deleteMut.mutate(a.id); }} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setModal(null)}>
          <div className="bg-white dark:bg-zinc-900 rounded-2xl p-6 w-[480px] shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-5">{modal.mode === 'create' ? 'New Announcement' : 'Edit Announcement'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input value={modal.data.title} onChange={e => setModal(m => m ? { ...m, data: { ...m.data, title: e.target.value } } : null)}
                  className="w-full border border-gray-200 dark:border-zinc-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-zinc-800" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Content</label>
                <textarea value={modal.data.content} onChange={e => setModal(m => m ? { ...m, data: { ...m.data, content: e.target.value } } : null)}
                  rows={4} className="w-full border border-gray-200 dark:border-zinc-700 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-zinc-800" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Expires at (optional)</label>
                <input type="datetime-local" value={modal.data.expires_at} onChange={e => setModal(m => m ? { ...m, data: { ...m.data, expires_at: e.target.value } } : null)}
                  className="w-full border border-gray-200 dark:border-zinc-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-zinc-800" />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={modal.data.is_active} onChange={e => setModal(m => m ? { ...m, data: { ...m.data, is_active: e.target.checked } } : null)} className="h-4 w-4 rounded" />
                <span className="text-sm font-medium">Active (show on homepage)</span>
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setModal(null)} className="flex-1 px-4 py-2 rounded-xl border border-gray-200 dark:border-zinc-700 text-sm font-medium">Cancel</button>
              <button onClick={handleSubmit} disabled={!modal.data.title.trim() || !modal.data.content.trim()} className="flex-1 px-4 py-2 rounded-xl bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
